import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { EMPTY, Observable, catchError, map, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { environment } from '@/environment';
import { IdentifyCustomerByAddressResponse } from '@/shared/interfaces';
import { IdentificationStatusType } from '../../../interfaces/service-status.model';
import { SessionService } from '@/shared/services/session.service';
import { SESSION_KEY } from '@/shared/services/session.constants';

@Injectable({
  providedIn: 'root',
})
export class IdentificationService {
  private identifyApi = `${environment.gcp?.baseUrl}${environment.gcp?.identificationUrl}`;
  private cachedDeflectionToken: string | null = null;

  constructor(
    private sessionService: SessionService,
    @Inject(PLATFORM_ID) private platformId: any,
    private router: Router,
    private http: HttpClient
  ) {}

  public getConversationId(): Observable<any> {
    const conversationId = this.getTokenFromStorage();
    if (conversationId) {
      const user = { identificationStatus: IdentificationStatusType.FULLY_IDENTIFIED };

      return of({
        body: user,
        headers: { 'DAPI-ConversationId': conversationId },
      });
    }

    const headers = this.setHeaders();
    const body = { postCode: '', lastName: '' };

    return this.http.post(this.identifyApi, body, { headers, observe: 'response' }).pipe(
      map((token: any) => {
        sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds('3000'));
        sessionStorage.setItem(this.getAuthTokenName(), token.headers['Dapi-Conversationid'] as string);
      })
    );
  }

  public findUser(data: any): Observable<any> {
    const headers = this.setHeaders();
    const body = { postCode: data.postcode, lastName: data.lastName };

    if (data.houseNumber) {
      body['houseNumber'] = data.houseNumber;
    }

    return this.http.post(this.identifyApi, body, { headers, observe: 'response' }).pipe(
      map((token: HttpResponse<any>) => {
        this.sessionService.removeData();
        const conversationId = token.headers.get('Dapi-Conversationid');

        sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds('3599'));
        sessionStorage.setItem(this.getAuthTokenName(), conversationId);

        this.sessionService.setData(SESSION_KEY.IDENTIFICATION, {
          account: { ...token.body },
          user: { ...body, identificationStatus: IdentifyCustomerByAddressResponse.ONE_RESULT },
        });

        return { body: { identificationStatusCode: IdentifyCustomerByAddressResponse.ONE_RESULT } };
      }),
      catchError((error: HttpErrorResponse) => {
        if (error?.error?.action === 'REQUEST_HOUSE_NUMBER' && error?.status === 404) {
          const errorMsg = { body: { identificationStatusCode: IdentifyCustomerByAddressResponse.MULTIPLE_RESULTS } };
          return of(errorMsg);
        }

        this.router.navigateByUrl(`/status/identification/identification-failed`);
        return EMPTY;
      })
    );
  }

  public findPingFedUser(bearer: string): Observable<any> {
    const headers = this.setHeaders(bearer);
    const conversationId = this.getTokenFromStorage();

    if (!conversationId) {
      return this.http.post(this.identifyApi, {}, { headers, observe: 'response' }).pipe(
        map((res: HttpResponse<any>) => {
          const conversationId = res.headers.get('Dapi-Conversationid');

          sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds('3599'));
          sessionStorage.setItem(this.getAuthTokenName(), conversationId);

          this.sessionService.setData(SESSION_KEY.IDENTIFICATION, {
            account: { ...res.body },
            user: { ...res.body, identificationStatus: IdentifyCustomerByAddressResponse.ONE_RESULT },
          });

          return of(true);
        }),
        catchError(() => {
          this.router.navigateByUrl(`/status/identification/identify`);
          return EMPTY;
        })
      );
    } else {
      const sessionId = this.sessionService.getData(SESSION_KEY.IDENTIFICATION);

      if (sessionId && !sessionId?.user) {
        this.sessionService.setData(SESSION_KEY.IDENTIFICATION, {
          ...sessionId,
          user: {
            postCode: sessionId?.account.postCode,
            houseNumber: '',
            lastName: '',
            identificationStatus: IdentifyCustomerByAddressResponse.ONE_RESULT,
          },
        });
      }
      return of(true);
    }
  }

  public findUserByMTD(ivrToken: string): Observable<any> {
    const headers = this.setHeaders(null, ivrToken);

    return this.http.post(this.identifyApi, {}, { headers, observe: 'response' }).pipe(
      map((res: HttpResponse<any>) => {
        const conversationId = res.headers.get('Dapi-Conversationid');

        sessionStorage.setItem(this.getAuthTokenExpiryName(), this.getTimestampFromSeconds('3599'));
        sessionStorage.setItem(this.getAuthTokenName(), conversationId);

        this.sessionService.setData(SESSION_KEY.IDENTIFICATION, {
          account: { ...res.body },
          user: { ...res.body, identificationStatus: IdentifyCustomerByAddressResponse.ONE_RESULT },
        });
        return res;
      })
    );
  }

  setHeaders(bearer: string = null, ivrToken: string = null) {
    const uuid = uuidv4();
    let headers = new HttpHeaders();

    if (bearer) {
      headers = headers.set('Authorization', `Bearer ${bearer}`);
      headers = headers.set('DAPI-OauthServer', environment.gcp?.oauthServer);
    }

    headers = headers.set('Content-Type', 'application/json; charset=utf-8');
    headers = headers.set('DAPI-CorrelationID', uuid);
    headers = headers.set('DAPI-RequestID', uuid);
    headers = headers.set('DAPI-RequestTimestamp', new Date().toISOString());
    headers = headers.set('DAPI-ChannelID', environment.gcp?.dapiChannelId);
    headers = headers.set('DAPI-OriginatorID', uuid);
    headers = headers.set('DAPI-ClientSecret', environment.gcp?.dapiClientSecret);
    headers = headers.set('DAPI-AppID', environment.gcp?.dapiAppId);

    if (ivrToken || this.cachedDeflectionToken) {
      this.cachedDeflectionToken = ivrToken || this.cachedDeflectionToken;
      headers = headers.set('DAPI-deflection', this.cachedDeflectionToken);
    }

    return headers;
  }

  getIPFromHeaders() {
    const location = window.document.location;
    const req = new XMLHttpRequest();
    // @ts-ignore
    req.open('HEAD', location, false);
    req.send(null);
    const ip = req.getResponseHeader('X-Virgin-IP');
    return ip ? ip : null;
  }

  public getTokenFromStorage(): any {
    if (isPlatformBrowser(this.platformId)) {
      const regToken = sessionStorage.getItem(this.getAuthTokenName());
      const expiry = sessionStorage.getItem(this.getAuthTokenExpiryName());
      const now = new Date().getTime();
      if ((regToken && expiry && parseInt(expiry, 10) > now) || (regToken && expiry === 'NaN')) {
        return regToken;
      }
    }
    return null;
  }

  public getTimestampFromSeconds(seconds): string {
    const expSec = seconds;
    const expDate = new Date();
    const expiryH = Math.floor(expSec / 3600);
    const expiryM = Math.floor((expSec % 3600) / 60);
    const expiryS = expSec % 60;
    expDate.setHours(expDate.getHours() + expiryH);
    expDate.setMinutes(expDate.getMinutes() + expiryM);
    expDate.setSeconds(expDate.getSeconds() + expiryS);
    return expDate.getTime().toString();
  }

  public getAuthTokenName() {
    return 'vmo2_sess_conversation_ID';
  }

  public getAuthTokenExpiryName() {
    return 'vmo2_sess_conversation_ID_expiry';
  }

  public getPingFedAccessToken() {
    return 'access_token';
  }
}
